import { Trophy, Target, Users, AlertCircle, CheckCircle, Clock, XCircle, Pencil, Check, X, Globe } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { EnrolledCompetition, EntryStatus } from '../hooks/useUserCompetitionEnrollment';
import { prefetchManager } from '../lib/prefetchManager';
import { ShareButton } from './ShareButton';
import { WhatsAppShareButton } from './WhatsAppShareButton';

interface CompetitionDashboardCardProps {
  competition: EnrolledCompetition;
  organizationSlug: string;
  organizationName: string;
  onNavigate: () => void;
  onUpdateEntryNickname?: (entryId: string, newNickname: string, competitionId: string) => Promise<{ success: boolean; error?: string }>;
}

function useCountdown(targetDate: Date | undefined) {
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  useEffect(() => {
    if (!targetDate) {
      setTimeRemaining('');
      return;
    }

    // Ensure targetDate is a Date object
    const deadline = targetDate instanceof Date ? targetDate : new Date(targetDate);

    // Check if the date is valid
    if (isNaN(deadline.getTime())) {
      setTimeRemaining('');
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const diff = deadline.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining('Deadline passed');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return timeRemaining;
}

interface EntryStatusRowProps {
  entry: EntryStatus;
  isEditing: boolean;
  editError: string | null;
  onStartEdit: () => void;
  competitionType?: string;
}

function EntryStatusRow({ 
  entry, 
  isEditing, 
  editError, 
  onStartEdit,
  competitionType
}: EntryStatusRowProps) {
  const gwText = entry.currentGameweekSurvived > 0 
    ? `GW${entry.currentGameweekSurvived}` 
    : '';

  const getStatusInfo = () => {
    if (entry.isKnockedOut) {
      return {
        bgClass: 'bg-red-50 dark:bg-red-900/20',
        statusClass: 'text-red-600 dark:text-red-400',
        icon: <XCircle className="w-3.5 h-3.5" />,
        text: 'Knocked Out'
      };
    }
    if (entry.needsPick) {
      return {
        bgClass: 'bg-amber-50 dark:bg-amber-900/20',
        statusClass: 'text-amber-600 dark:text-amber-400',
        icon: <AlertCircle className="w-3.5 h-3.5" />,
        text: competitionType === 'worldcup' ? 'Action Required' : 'Pick Required'
      };
    }
    return {
      bgClass: 'bg-green-50 dark:bg-green-900/20',
      statusClass: 'text-green-600 dark:text-green-400',
      icon: <CheckCircle className="w-3.5 h-3.5" />,
      text: competitionType === 'worldcup' ? 'Up to Date' : 'Pick Submitted'
    };
  };

  const status = getStatusInfo();

  return (
    <div className={`py-1.5 px-2 rounded ${status.bgClass}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{entry.entryNickname}</span>
          <button
            onClick={(e) => { e.stopPropagation(); onStartEdit(); }}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
            title="Edit entry name"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {gwText && (
            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded flex-shrink-0">
              {gwText}
            </span>
          )}
        </div>
        <span className={`flex items-center gap-1 text-xs font-medium ${status.statusClass} flex-shrink-0`}>
          {status.icon}
          {status.text}
        </span>
      </div>
      {editError && isEditing && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">{editError}</p>
      )}
    </div>
  );
}

export function CompetitionDashboardCard({ competition, organizationSlug, organizationName, onNavigate, onUpdateEntryNickname }: CompetitionDashboardCardProps) {
  const isLMS = competition.competitionType === 'lms';
  const isWorldCup = competition.competitionType === 'worldcup';
  const showPrizePot = competition.entryFeeEnabled
    || (competition.initialPrizePot && competition.initialPrizePot > 0)
    || (competition.manualPrizePotOverride && competition.manualPrizePotOverride > 0);
  const prizePotCurrencySymbol = competition.prizePotCurrency === 'gbp' ? '£' : competition.prizePotCurrency === 'usd' ? '$' : '€';
  const prizePotAmount = `${prizePotCurrencySymbol}${((competition.manualPrizePotOverride || competition.calculatedPrizePot || 0) / 100).toFixed(2)}`;
  const countdown = useCountdown(competition.nextDeadline);
  const hasMultipleEntries = (isLMS || isWorldCup) && competition.entries && competition.entries.length > 1;
  
  // Edit state
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  
  // Optimistic UI updates for entry nicknames
  const [localNicknameUpdates, setLocalNicknameUpdates] = useState<Record<string, string>>({});

  // Apply local nickname updates to entries for display
  const displayEntries = competition.entries?.map(entry => ({
    ...entry,
    entryNickname: localNicknameUpdates[entry.entryId] ?? entry.entryNickname
  }));

  const handleStartEdit = (entry: EntryStatus) => {
    // Use the display nickname (which may include local updates)
    const displayNickname = localNicknameUpdates[entry.entryId] ?? entry.entryNickname;
    setEditingEntryId(entry.entryId);
    setEditValue(displayNickname);
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditingEntryId(null);
    setEditValue('');
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingEntryId || !onUpdateEntryNickname) return;
    
    const trimmedValue = editValue.trim();
    setEditSaving(true);
    setEditError(null);
    
    const result = await onUpdateEntryNickname(editingEntryId, trimmedValue, competition.id);
    
    setEditSaving(false);
    
    if (result.success) {
      // Apply optimistic update immediately
      setLocalNicknameUpdates(prev => ({
        ...prev,
        [editingEntryId]: trimmedValue
      }));
      setEditingEntryId(null);
      setEditValue('');
    } else {
      setEditError(result.error || 'Failed to update');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  // Find the entry being edited for the modal (use display entries with local updates)
  const editingEntry = displayEntries?.find(e => e.entryId === editingEntryId);

  const handleMouseEnter = () => {
    if (competition.entryFeeEnabled && !competition.hasAccess) {
      prefetchManager.prefetch('stripe', async () => {
        await Promise.all([
          import('@stripe/stripe-js'),
          import('@stripe/react-stripe-js'),
          import('./StripeProductCard').then(() => console.log('[CompetitionCard] Prefetched StripeProductCard component'))
        ]);
        console.log('[CompetitionCard] Prefetched Stripe SDK and React components');
      });
    }
  };

  const getStatusColor = () => {
    if (competition.needsAction) return 'border-amber-500 bg-amber-50 dark:bg-amber-900/20';
    if (competition.isKnockedOut) return 'border-red-500 bg-red-50 dark:bg-red-900/20';
    if (isLMS) return 'border-green-500 bg-green-50 dark:bg-green-900/20';
    if (isWorldCup) return 'border-amber-500 bg-amber-50 dark:bg-amber-900/20';
    return 'border-blue-500 bg-blue-50 dark:bg-blue-900/20';
  };

  const getStatusText = () => {
    if (competition.isKnockedOut) return 'Knocked Out';
    if (competition.needsAction) return 'Action Required';
    if (isLMS) return 'Pick Submitted';
    return 'Up to Date';
  };

  const getStatusIcon = () => {
    if (competition.needsAction) return <AlertCircle className="w-7 h-7 text-amber-600 dark:text-amber-400" />;
    if (competition.isKnockedOut) return <AlertCircle className="w-7 h-7 text-red-600 dark:text-red-400" />;
    return <CheckCircle className="w-7 h-7 text-green-600 dark:text-green-400" />;
  };

  const getActionButtonText = () => {
    if (competition.isKnockedOut) {
      return competition.status === 'finished' ? 'View Results' : 'View Competition';
    }
    if (competition.needsAction) {
      if (isLMS && competition.entriesNeedingPicks && competition.entriesNeedingPicks > 1) {
        return `Make ${competition.entriesNeedingPicks} Picks`;
      }
      return isLMS ? 'Make Your Pick' : 'Make Predictions';
    }
    return 'Continue Playing';
  };

  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when modal opens
  useEffect(() => {
    if (editingEntryId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingEntryId]);

  return (
    <>
      <button
        onClick={onNavigate}
        onMouseEnter={handleMouseEnter}
        className={`group relative bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border-2 transition-all hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] text-left w-full flex flex-col items-stretch ${getStatusColor()}`}
      >
        <div className={`px-4 py-4 sm:px-6 sm:py-5 ${
          isWorldCup
            ? 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20'
            : isLMS
              ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20'
              : 'bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20'
        }`}>
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg shadow-md ${
                  isWorldCup
                    ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                    : isLMS
                      ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                      : 'bg-gradient-to-br from-blue-500 to-cyan-600'
                }`}>
                  {isWorldCup ? (
                    <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  ) : isLMS ? (
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  ) : (
                    <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  )}
                </div>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  isWorldCup
                    ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                    : isLMS
                      ? 'bg-green-500/20 text-green-700 dark:text-green-300'
                      : 'bg-blue-500/20 text-blue-700 dark:text-blue-300'
                }`}>
                  {isWorldCup ? 'World Cup' : isLMS ? 'Last Man Standing' : 'Predictor'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon()}
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              {competition.name}
            </h3>
          </div>

          {/* Per-entry status for competitions with multiple entries */}
          {hasMultipleEntries && displayEntries ? (
            <div className="space-y-1.5 mb-2">
              {displayEntries.map((entry) => (
                <EntryStatusRow 
                  key={entry.entryId} 
                  entry={entry}
                  isEditing={editingEntryId === entry.entryId}
                  editError={editError}
                  onStartEdit={() => handleStartEdit(entry)}
                  competitionType={competition.competitionType}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs sm:text-sm font-medium">
                <span className={`px-2 py-1 rounded-full ${competition.needsAction ? 'bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100' : competition.isKnockedOut ? 'bg-red-200 dark:bg-red-800 text-red-900 dark:text-red-100' : 'bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-100'}`}>
                  {getStatusText()}
                </span>
              </div>
            </div>
          )}
          
          {countdown && !competition.isKnockedOut && !isWorldCup && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 mt-2">
              <Clock className="w-3.5 h-3.5" />
              <span className="font-medium">Next deadline: {countdown}</span>
            </div>
          )}
        </div>

        <div className="px-4 py-4 sm:px-6 sm:py-5 bg-white dark:bg-gray-800 flex-1 flex flex-col justify-end">
          {isLMS ? (
            <div className="mb-4">
              {/* Progress bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-gray-600 dark:text-gray-400 font-medium">Participants Remaining</span>
                  <span className="font-bold text-gray-900 dark:text-white">
                    {competition.remainingParticipantCount ?? competition.participantCount} / {competition.participantCount}
                    {!!competition.participantCount && competition.remainingParticipantCount !== undefined && (
                      <span className="text-gray-500 dark:text-gray-400 font-normal ml-1">
                        ({Math.round((competition.remainingParticipantCount / competition.participantCount) * 100)}%)
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${competition.participantCount && competition.remainingParticipantCount !== undefined
                        ? Math.round((competition.remainingParticipantCount / competition.participantCount) * 100)
                        : 0}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          ) : isWorldCup ? (
            showPrizePot ? (
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Submitted Entries</p>
                    <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                      {competition.wcSubmittedCount ?? '-'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Prize Pot</p>
                    <p className="text-sm sm:text-base font-semibold text-amber-700 dark:text-amber-300">
                      {prizePotAmount}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Submitted Entries</p>
                    <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                      {competition.wcSubmittedCount ?? '-'}
                    </p>
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Participants</p>
                  <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                    {competition.participantCount != null ? competition.participantCount : '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Predictions</p>
                  <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                    {competition.predictionCount}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Prize Pot Display (LMS / Predictor only — World Cup shows prize pot in stats grid) */}
          {showPrizePot && !isWorldCup && (
            <div className="mb-4 p-3 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  <span className="text-xs font-semibold text-amber-900 dark:text-amber-100">
                    Prize Pot
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-base font-bold text-amber-700 dark:text-amber-300">
                    {prizePotAmount}
                  </div>
                </div>
              </div>
            </div>
          )}

          {competition.entryFeeEnabled && competition.hasAccess && (
            <div className="mb-4 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-xs text-green-700 dark:text-green-300 font-medium flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Entry Fee Paid
              </p>
            </div>
          )}

          <div className={`text-center py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-all ${
            isWorldCup
              ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white group-hover:opacity-90'
              : isLMS
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white group-hover:opacity-90'
                : 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white group-hover:opacity-90'
          }`}>
            {getActionButtonText()}
          </div>

          {/* Share buttons - hidden for World Cup once competition has started */}
          {!isWorldCup && (
            <div className="flex items-center gap-2 mt-3 w-full">
              <ShareButton
                competition={competition}
                organizationSlug={organizationSlug}
                variant="button"
                size="sm"
                className="flex-1 justify-center"
              />
              <WhatsAppShareButton
                competition={competition}
                organizationSlug={organizationSlug}
                organizationName={organizationName}
                variant="button"
                size="sm"
                className="flex-1 justify-center"
              />
            </div>
          )}
        </div>
      </button>

      {/* Edit Entry Modal */}
      {editingEntryId && editingEntry && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCancelEdit();
          }}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Edit Entry Name
              </h3>
              <button
                onClick={handleCancelEdit}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Entry Name
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={editSaving}
                  className="w-full px-4 py-3 text-base rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
                  placeholder="Enter nickname..."
                  maxLength={50}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  2-50 characters
                </p>
              </div>
              
              {editError && (
                <p className="text-sm text-red-600 dark:text-red-400">{editError}</p>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={handleCancelEdit}
                  disabled={editSaving}
                  className="flex-1 px-4 py-3 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={editSaving || editValue.trim().length < 2}
                  className="flex-1 px-4 py-3 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {editSaving ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
